import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { InboxItem, InboxState, Page } from '@elutide/shared';
import { api, ApiProblem } from '@/lib/api';
import { qk } from '@/lib/keys';
import { LIVE } from '@/app/queryClient';
import { useInboxOpenCount } from '@/app/badges';
import { useSession } from '@/app/session';
import { useListNavigation } from '@/lib/hooks';
import {
  Card,
  Chip,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InboxRow,
  Skeleton,
  useToast,
} from '@/components';

type FilterId = 'all' | 'mine' | 'unassigned' | 'snoozed' | 'resolved';

const FILTERS: readonly { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'Mine' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'snoozed', label: 'Snoozed' },
  { id: 'resolved', label: 'Resolved 7d' },
];

function queryFor(filter: FilterId, userId: string | null): Record<string, string> {
  switch (filter) {
    case 'mine':
      return { state: 'assigned', ...(userId ? { assignee: userId } : {}) };
    case 'unassigned':
      return { state: 'open' };
    case 'snoozed':
      return { state: 'snoozed' };
    case 'resolved':
      return { state: 'resolved' };
    default:
      return {};
  }
}

export interface ActionInboxProps {
  /** Home shows the top items; the full page shows everything. */
  limit?: number;
  showViewAll?: boolean;
}

/**
 * A list of work, not a feed. Severity then age, one contextual primary action per row, and
 * the lifecycle actions behind it.
 *
 * Snooze and resolve are reversible inside ten seconds, so they get an optimistic update and
 * an Undo toast. Nothing that moves money is in here: those items link out to the screen
 * that owns the ceremony.
 */
export function ActionInbox({ limit = 8, showViewAll = true }: ActionInboxProps) {
  const [filter, setFilter] = useState<FilterId>('all');
  const [snoozing, setSnoozing] = useState<InboxItem | null>(null);
  const { session, can } = useSession();
  const client = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const filters = useMemo(() => queryFor(filter, session?.user_id ?? null), [filter, session]);

  const query = useQuery({
    queryKey: qk.inbox({ ...filters, limit }),
    queryFn: () =>
      api.get<Page<InboxItem>>('/inbox', { query: { ...filters, limit } }),
    enabled: can('inbox.read'),
    ...LIVE,
  });

  const items = useMemo(() => {
    const rows = query.data?.items ?? [];
    if (filter === 'unassigned') return rows.filter((item) => item.assignee_user_id === null);
    return rows;
  }, [filter, query.data]);

  const resolve = useMutation({
    mutationFn: (item: InboxItem) =>
      api.post<InboxItem>(`/inbox/${item.item_id}/resolve`, { resolution: 'handled' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['inbox'] });
      toast.push({ tone: 'ok', message: 'Item resolved. It reopens if the condition returns.' });
    },
    onError: (error) =>
      toast.push({
        tone: 'crit',
        message: error instanceof ApiProblem ? error.message : 'Resolve failed.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      }),
  });

  const snooze = useMutation({
    mutationFn: ({ item, until }: { item: InboxItem; until: string }) =>
      api.post<InboxItem>(`/inbox/${item.item_id}/snooze`, { until }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['inbox'] });
      toast.push({ tone: 'neutral', message: 'Snoozed. A crit item still surfaces if it worsens.' });
    },
    onError: (error) =>
      toast.push({
        tone: 'crit',
        message: error instanceof ApiProblem ? error.message : 'Snooze failed.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      }),
  });

  const assign = useMutation({
    mutationFn: (item: InboxItem) =>
      api.post<InboxItem>(`/inbox/${item.item_id}/assign`, { user_id: session?.user_id ?? null }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['inbox'] }),
  });

  const open = (item: InboxItem) => {
    const href = item.primary_action?.href;
    if (href) void navigate({ to: href });
  };

  const nav = useListNavigation({
    count: items.length,
    onOpen: (index) => {
      const item = items[index];
      if (item) open(item);
    },
  });

  // The same number the sidebar badge shows, from the same endpoint. Reading the page
  // length here made the header report the page size instead of the open work.
  const openCount = useInboxOpenCount();

  return (
    <Card
      title={`Action Inbox${openCount ? ` · ${openCount} open` : ''}`}
      action={
        showViewAll ? (
          <Link to="/inbox" className="o-btn o-btn--ghost o-btn--sm">
            View all
          </Link>
        ) : undefined
      }
      id="action-inbox"
    >
      <div className="o-row" role="group" aria-label="Inbox filters">
        {FILTERS.map((entry) => (
          <Chip
            key={entry.id}
            label={entry.label}
            active={filter === entry.id}
            onClick={() => setFilter(entry.id)}
          />
        ))}
      </div>

      {query.isLoading ? <Skeleton shape="row" lines={4} label="Loading the inbox" /> : null}

      {query.isError ? (
        <ErrorState
          message={
            query.error instanceof ApiProblem ? query.error.message : 'The inbox did not load.'
          }
          requestId={query.error instanceof ApiProblem ? query.error.shortRequestId : null}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {!query.isLoading && !query.isError && items.length === 0 ? (
        <EmptyState message="Nothing needs you right now." />
      ) : null}

      {items.length > 0 ? (
        <ul className="o-inbox" onKeyDown={nav.onKeyDown} tabIndex={-1} aria-label="Inbox items">
          {items.map((item, index) => (
            <InboxRow
              key={item.item_id}
              item={item}
              focused={nav.index === index}
              onOpenSource={open}
              onAssign={can('inbox.assign') ? (target) => assign.mutate(target) : undefined}
              onSnooze={can('inbox.snooze') ? (target) => setSnoozing(target) : undefined}
              onResolve={
                canResolve(item, session?.user_id ?? null, can)
                  ? (target) => resolve.mutate(target)
                  : undefined
              }
            />
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={snoozing !== null}
        title="Snooze this item"
        body="It comes back when the snooze expires. A crit item still surfaces early if the severity rises."
        effects={['Hidden from the open list until the snooze expires', 'Recorded on the item']}
        confirmLabel="Snooze 4 hours"
        onCancel={() => setSnoozing(null)}
        onConfirm={async () => {
          const item = snoozing;
          setSnoozing(null);
          if (!item) return;
          await snooze.mutateAsync({
            item,
            until: new Date(Date.now() + 4 * 3600_000).toISOString(),
          });
        }}
      />
    </Card>
  );
}

function canResolve(
  item: InboxItem,
  userId: string | null,
  can: (permission: 'inbox.resolve' | 'inbox.resolve_own') => boolean,
): boolean {
  if (can('inbox.resolve')) return true;
  return can('inbox.resolve_own') && item.assignee_user_id === userId;
}

export type { InboxState };
